import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as copilotApi from '../../services/copilot-api.js';
import type { CopilotOp } from '../../services/copilot-api.js';

interface ChatLine { role: 'user' | 'assistant'; text: string; meta?: string }

/**
 * Goal-Tree-Copilot (Opus). Chat-Panel rechts neben dem Zielbaum. Du beschreibst in Worten,
 * was du willst — Opus legt Vision/Säulen/Objectives inkl. Constraints direkt an (Hybrid:
 * create sofort, update/delete als Bestätigungs-Karten). Sendet `tree-changed` bei Änderungen.
 */
@customElement('cp-goal-copilot')
export class CpGoalCopilot extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;
  @state() private _lines: ChatLine[] = [];
  @state() private _pending: CopilotOp[] = [];
  @state() private _input = '';
  @state() private _loading = false;
  @state() private _error = '';

  private _emitChanged(): void {
    this.dispatchEvent(new CustomEvent('tree-changed', { bubbles: true, composed: true }));
  }

  private _opLabel(op: CopilotOp): string {
    const kind = op.op === 'update_node' ? 'Ändern' : op.op === 'delete_node' ? 'Löschen' : op.op;
    const detail = op.op === 'delete_node' ? `Knoten #${op.id}` : `#${op.id}${op.text ? ` → „${op.text}"` : ''}`;
    return `${kind}: ${detail}`;
  }

  private _appliedSummary(applied: copilotApi.GoalTreeAssistResponse['applied']): string {
    if (!applied.length) return '';
    return applied.map((a) => {
      const t = a.op.op.replace('create_', '');
      return `+${t} „${a.node?.text ?? a.op.text ?? ''}"`;
    }).join(' · ');
  }

  private async _send(): Promise<void> {
    const msg = this._input.trim();
    if (!msg || this._loading || !this.companyId) return;
    this._input = '';
    this._error = '';
    this._lines = [...this._lines, { role: 'user', text: msg }];
    this._loading = true;
    try {
      const res = await copilotApi.goalTreeAssist(this.companyId, msg);
      const summary = this._appliedSummary(res.applied);
      const metaParts = [summary, ...res.errors];
      this._lines = [...this._lines, { role: 'assistant', text: res.reply, meta: metaParts.filter(Boolean).join(' · ') || undefined }];
      this._pending = [...this._pending, ...res.pending];
      if (res.applied.length) this._emitChanged();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler';
    } finally {
      this._loading = false;
    }
  }

  private async _applyPending(op: CopilotOp): Promise<void> {
    this._loading = true; this._error = '';
    try {
      await copilotApi.applyGoalTreeOp(this.companyId, op);
      this._pending = this._pending.filter((p) => p !== op);
      this._emitChanged();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler';
    } finally {
      this._loading = false;
    }
  }

  private _discardPending(op: CopilotOp): void {
    this._pending = this._pending.filter((p) => p !== op);
  }

  private _onKey(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void this._send(); }
  }

  override render() {
    return html`
      <style>
        .cop { display: flex; flex-direction: column; height: 100%; min-height: 420px; border: 1px solid #e3e3e3; border-radius: 10px; background: #fff; overflow: hidden; font-family: system-ui, sans-serif; color: #1a2330; }
        .cop-head { padding: .7rem .9rem; border-bottom: 1px solid #eee; background: #0e1b2e; color: #cfe8ff; }
        .cop-head h3 { margin: 0; font-size: .95rem; display: flex; align-items: center; gap: .5rem; }
        .cop-head .badge { font-size: .65rem; background: #1a7fd4; color: #fff; padding: .1rem .45rem; border-radius: 999px; letter-spacing: .04em; }
        .cop-head p { margin: .25rem 0 0; font-size: .74rem; color: #7fa8cc; }
        .cop-body { flex: 1; overflow: auto; padding: .8rem; display: flex; flex-direction: column; gap: .6rem; }
        .cop-empty { color: #999; font-size: .85rem; text-align: center; margin-top: 1.5rem; line-height: 1.5; }
        .cop-empty code { background: #f2f4f7; padding: .05rem .3rem; border-radius: 4px; font-size: .8rem; }
        .cop-line { max-width: 92%; padding: .5rem .7rem; border-radius: 10px; font-size: .86rem; line-height: 1.4; white-space: pre-wrap; }
        .cop-user { align-self: flex-end; background: #1a7fd4; color: #fff; border-bottom-right-radius: 3px; }
        .cop-assistant { align-self: flex-start; background: #f1f4f8; color: #1a2330; border-bottom-left-radius: 3px; }
        .cop-meta { font-size: .72rem; color: #2e7d32; margin-top: .3rem; }
        .cop-pending { border: 1px dashed #b8860b; border-radius: 8px; padding: .5rem .6rem; background: #fffbf0; }
        .cop-pending-label { font-size: .82rem; color: #7a5c00; margin-bottom: .4rem; }
        .cop-pending-btns { display: flex; gap: .4rem; }
        .cop-apply { cursor: pointer; border: none; background: #b8860b; color: #fff; border-radius: 6px; padding: .3rem .8rem; font-size: .8rem; }
        .cop-discard { cursor: pointer; border: 1px solid #ccc; background: #fff; color: #666; border-radius: 6px; padding: .3rem .8rem; font-size: .8rem; }
        .cop-foot { border-top: 1px solid #eee; padding: .6rem; display: flex; gap: .5rem; }
        .cop-foot textarea { flex: 1; resize: none; border: 1px solid #d6dbe2; border-radius: 8px; padding: .5rem .6rem; font-family: inherit; font-size: .86rem; height: 2.6rem; color: #1a2330; background: #fff; caret-color: #1a7fd4; }
        .cop-foot textarea::placeholder { color: #9aa6b2; }
        .cop-send { cursor: pointer; border: none; background: #0e1b2e; color: #fff; border-radius: 8px; padding: 0 1rem; font-weight: 600; }
        .cop-send:disabled { opacity: .5; cursor: default; }
        .cop-err { color: #c33; font-size: .8rem; padding: 0 .8rem .4rem; }
        .cop-typing { color: #888; font-size: .82rem; font-style: italic; align-self: flex-start; }
      </style>
      <div class="cop">
        <div class="cop-head">
          <h3>Ziel-Copilot <span class="badge">Opus</span></h3>
          <p>Beschreib dein Ziel in Worten — ich lege Säulen, Objectives & Constraints an.</p>
        </div>
        <div class="cop-body">
          ${this._lines.length === 0 ? html`
            <div class="cop-empty">
              Z.B.<br><code>Säule „Akquise" mit Constraint Vertragsart=Festanstellung, dazu Objective „20 Platzierungen pro Quartal"</code>
            </div>
          ` : nothing}
          ${this._lines.map((l) => html`
            <div class="cop-line ${l.role === 'user' ? 'cop-user' : 'cop-assistant'}">
              ${l.text}
              ${l.meta ? html`<div class="cop-meta">${l.meta}</div>` : nothing}
            </div>
          `)}
          ${this._pending.map((op) => html`
            <div class="cop-pending">
              <div class="cop-pending-label">Bestätigen: ${this._opLabel(op)}</div>
              <div class="cop-pending-btns">
                <button class="cop-apply" ?disabled=${this._loading} @click=${() => void this._applyPending(op)}>Übernehmen</button>
                <button class="cop-discard" @click=${() => this._discardPending(op)}>Verwerfen</button>
              </div>
            </div>
          `)}
          ${this._loading ? html`<div class="cop-typing">Opus denkt…</div>` : nothing}
        </div>
        ${this._error ? html`<div class="cop-err">${this._error}</div>` : nothing}
        <div class="cop-foot">
          <textarea
            .value=${this._input}
            placeholder="Nachricht an den Copilot…"
            @input=${(e: Event) => { this._input = (e.target as HTMLTextAreaElement).value; }}
            @keydown=${this._onKey}
          ></textarea>
          <button class="cop-send" ?disabled=${this._loading || !this._input.trim()} @click=${() => void this._send()}>↑</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-goal-copilot': CpGoalCopilot;
  }
}
